import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { videoCategories } from "@/mocks/categories";

export default function SiteNavbar() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const [query, setQuery] = useState("");

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = query.trim();
    if (!keyword) return;
    navigate(`/search?query=${encodeURIComponent(keyword)}`);
    setMobileSearch(false);
    setMobileOpen(false);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-label transition-colors whitespace-nowrap ${
      isActive ? "text-primary-600 font-semibold" : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center h-16 gap-4 md:gap-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-movie-2-fill text-xl"></i>
            </span>
            <span className="text-lg font-heading font-bold text-foreground-950 whitespace-nowrap">
              樱桃视频
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            <NavLink to="/" className={navLinkClass} end>
              首页
            </NavLink>
            <div
              className="relative"
              onMouseEnter={() => setCatOpen(true)}
              onMouseLeave={() => setCatOpen(false)}
            >
              <button
                type="button"
                className="flex items-center gap-1 text-sm font-label text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                onClick={() => setCatOpen((prev) => !prev)}
              >
                分类导航
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full pt-3">
                  <div className="w-56 rounded-lg bg-background-50 border border-background-200 p-2">
                    {videoCategories.map((category) => (
                      <Link
                        key={category.slug}
                        to={`/category/${category.slug}`}
                        onClick={() => setCatOpen(false)}
                        className="flex items-center gap-3 rounded-md px-3 py-2.5 hover:bg-background-100 transition-colors"
                      >
                        <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                          <i
                            className={
                              category.slug === "short-video"
                                ? "ri-play-circle-line"
                                : category.slug === "net-movie"
                                ? "ri-film-line"
                                : "ri-mic-line"
                            }
                          ></i>
                        </span>
                        <span className="text-sm text-foreground-800">{category.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/about" className={navLinkClass}>
              关于我们
            </NavLink>
          </nav>

          {/* Desktop search */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 justify-end">
            <div className="relative w-full max-w-xs">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                type="text"
                placeholder="搜索视频标题或简介"
                className="w-full rounded-full bg-background-100 border border-background-200 pl-9 pr-4 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
              />
            </div>
          </form>

          {/* Mobile actions */}
          <div className="flex md:hidden items-center gap-1 ml-auto">
            <button
              type="button"
              aria-label="搜索"
              onClick={() => setMobileSearch(true)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-search-line text-xl"></i>
            </button>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMobileOpen((prev) => !prev)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-3">
          <nav className="flex flex-col">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="py-3 text-base text-foreground-800 border-b border-background-100"
            >
              首页
            </Link>
            <div className="py-3 border-b border-background-100">
              <p className="text-xs font-label text-foreground-400 mb-2">分类导航</p>
              <div className="flex flex-wrap gap-2">
                {videoCategories.map((category) => (
                  <Link
                    key={category.slug}
                    to={`/category/${category.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="rounded-full bg-background-100 px-3 py-1.5 text-sm text-foreground-700"
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/about"
              onClick={() => setMobileOpen(false)}
              className="py-3 text-base text-foreground-800"
            >
              关于我们
            </Link>
          </nav>
        </div>
      )}

      {/* Mobile full-screen search */}
      {mobileSearch && (
        <div className="md:hidden fixed inset-0 z-[60] bg-background-50 flex flex-col">
          <div className="flex items-center gap-3 px-4 h-16 border-b border-background-200">
            <button
              type="button"
              aria-label="关闭搜索"
              onClick={() => setMobileSearch(false)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-2xl"></i>
            </button>
            <span className="text-sm font-label text-foreground-500">搜索视频</span>
          </div>
          <form onSubmit={handleSearch} className="p-4">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                type="text"
                placeholder="输入关键词，如：美食、科幻"
                className="w-full rounded-full bg-background-100 border border-background-200 pl-9 pr-4 py-3 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="mt-4 w-full rounded-md bg-primary-500 py-3 text-sm font-label font-medium text-background-50 whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>
      )}
    </header>
  );
}